import {
  DynamicDocumentItemCustomType,
  DynamicDocumentItem,
  InstallationModuleData,
  Material,
  InstallationModule,
  SerialNumber,
  Inverter
} from "@/types";

import store from "@/store/store";

type ValidatorResult = boolean | string | string[];
type Validator = (data: InstallationModule) => ValidatorResult;

function noop(module: InstallationModuleData): ValidatorResult {
  return true;
}

function isValidPanelType(module: InstallationModule): ValidatorResult {
  if (!module?.data?.panelId) {
    return "Geen paneel geselecteerd";
  }

  return true;
}

function isValidPanelLocation(module: InstallationModule): ValidatorResult {
  if (!module?.data?.locationPanels) {
    return "Geen paneel locatie geselecteerd";
  }

  return true;
}

function isValidPanelOrientation(module: InstallationModule): ValidatorResult {
  if (!module?.data?.panelOrientation) {
    return "Geen paneeloriëntatie geselecteerd";
  }

  return true;
}

function isValidInverterType(module: InstallationModule): ValidatorResult {
  if (!module?.data?.inverterId) {
    return "Geen omvormer geselecteerd";
  }

  return true;
}

function hasDuplicate(item: SerialNumber, items: SerialNumber[]) {
  const itemIndex = items.indexOf(item);

  const dupeIndex = items.findIndex(
    (val, index) => val.serialNumber === item.serialNumber && index !== itemIndex
  );

  return dupeIndex >= 0;
}

function isValidInverterSerialNumbers(module: InstallationModule): ValidatorResult {
  const patterns: string[] = (store.state.materials.inverters.items as Material[])
    .flatMap(item => item.patterns ?? [])
    .filter(Boolean);

  // uncomment but preserve this line: inverterSerialNumbers should not validate against number of panels?
  // const gridSize = module?.data?.numberOfPanels ?? 0;
  const gridSize =
    module?.data?.inverterConfiguration?.reduce((acc, inverter) => {
      return acc + inverter.amount;
    }, 0) || 0;
  const items: SerialNumber[] = module?.data?.inverterSerialNumbers ?? [];

  const complete =
    items.length === gridSize &&
    items.every(
      sn =>
        !patterns.length ||
        (!!store.getters["materials/inverters/findItemBySn"](sn.serialNumber) &&
          !hasDuplicate(sn, items))
    );

  if (!complete) return "Serienummers van omvormers nog niet volledig en/of correct ingevuld";

  return true;
}

function isValidMeterAssociation(module: InstallationModule): ValidatorResult {
  const messages: string[] = [];

  // Enforced only when the meterAssociation item is required: always for
  // METER_MAAS-REGULAR, and for METER-REGULAR when "Is er spanning op de woning?" is Ja
  // (see document.meter.regular.json). When the item is optional this message is ignored,
  // because isValid = !isRequired || validation === true (see deliveryDocuments mapItem).
  if (
    (module.type === "METER_MAAS-REGULAR" || module.type === "METER-REGULAR") &&
    !module.flags?.hasAssociation
  ) {
    messages.push("Meter nog niet gekoppeld");
  }

  if (module.type === "METER_MAAS-REGULAR" && !module.flags?.hasBreakerOn) {
    const serialNumber = module.data?.meterSerialNumbers?.[0]?.serialNumber;
    const hasBreaker = store.getters["materials/meters/hasBreaker"](serialNumber);

    if (hasBreaker) {
      messages.push("Schakel de breaker aan");
    }
  }

  return !messages.length || messages;
}

// Validation-only item (no renderer; the test button lives in MeterAssociation.vue).
// Enforced only when the productionTest item is required: for METER-REGULAR when
// "Is het systeem met panelen helemaal aangesloten?" is Ja (see document.meter.regular.json).
function isValidProductionTest(module: InstallationModule): ValidatorResult {
  if (module.type === "METER-REGULAR" && !module.flags?.hasProductionTest) {
    return "Productietest nog niet uitgevoerd";
  }

  return true;
}

function isValidMeterSerialNumber(module: InstallationModule): ValidatorResult {
  const patterns: string[] = (store.state.materials.meters.items as Material[]).flatMap(
    item => item.types?.flatMap(type => type.patterns ?? []) ?? []
  );

  const items: SerialNumber[] = module?.data?.meterSerialNumbers ?? [];

  const complete =
    items.length === 1 &&
    items.every(
      sn => !patterns.length || !!store.getters["materials/meters/findItemBySn"](sn.serialNumber)
    );

  if (!complete) return "Serienummer van Wocozon meter niet (correct) ingevuld";

  return true;
}

function isValidMeterSerialNumberEnphaseGateway(module: InstallationModule): ValidatorResult {
  const patterns: string[] = (store.state.materials.meters.items as Material[]).flatMap(
    item => item.types?.flatMap(type => type.patterns ?? []) ?? []
  );

  const items: SerialNumber[] = module?.data?.meterSerialNumbersEnphaseGateway ?? [];

  const complete =
    items.length === 1 &&
    items.every(
      sn => !patterns.length || !!store.getters["materials/meters/findItemBySn"](sn.serialNumber)
    );

  if (!complete) return "Serienummer van Enphase gateway niet (correct) ingevuld";

  return true;
}

function isValidMeterSerialNumberEnphaseRelay(module: InstallationModule): ValidatorResult {
  const patterns: string[] = (store.state.materials.meters.items as Material[]).flatMap(
    item => item.types?.flatMap(type => type.patterns ?? []) ?? []
  );

  const items: SerialNumber[] = module?.data?.meterSerialNumbersEnphaseRelay ?? [];

  const complete =
    items.length === 1 &&
    items.every(
      sn => !patterns.length || !!store.getters["materials/meters/findItemBySn"](sn.serialNumber)
    );

  if (!complete) return "Serienummer van Enphase relay niet (correct) ingevuld";

  return true;
}

function isSufficientInverterCapacity(inverters: Inverter[], numberOfPanels: number) {
  const totalCapacity = inverters.reduce((total, inverter) => {
    return total + inverter.amount * inverter.max_panel_count;
  }, 0);

  return totalCapacity >= numberOfPanels;
}

function isValidAmountOfInverters(module: InstallationModule): ValidatorResult {
  if (module.data?.inverterConfiguration?.length === 0) {
    return "Vul het type omvormers in.";
  }

  if (
    module.data?.numberOfPanels &&
    module.data?.inverterConfiguration &&
    !isSufficientInverterCapacity(module.data?.inverterConfiguration, module.data?.numberOfPanels)
  ) {
    return `Aantal omvormers is te laag voor ${module.data?.numberOfPanels} panelen.`;
  }

  return true;
}

function isValidSolarGrid(module: InstallationModule): ValidatorResult {
  const amountFilled = module.data?.grid?.length ?? 0;

  if (amountFilled < module.data!.numberOfPanels!) {
    return `Er zijn nog niet voldoende panelen ingevuld (${amountFilled}/${module.data!
      .numberOfPanels!})`;
  }

  return true;
}

function isValidNumberOfBus(module: InstallationModule): ValidatorResult {
  const numberOfBus = module.data?.numberOfBus;

  if (!numberOfBus) {
    return "Vul het nummer van de bus in";
  }

  return true;
}

const validators: Record<DynamicDocumentItemCustomType, Validator> = {
  panelAmount: noop,
  panelType: isValidPanelType,
  panelGridSize: noop,
  panelGrid: isValidSolarGrid,
  panelLocation: isValidPanelLocation,
  panelOrientation: isValidPanelOrientation,
  inverterSerialNumberGrid: noop,
  inverterSerialNumbers: isValidInverterSerialNumbers,
  inverterType: isValidInverterType,
  meterAssociation: isValidMeterAssociation,
  productionTest: isValidProductionTest,
  meterSerialNumber: isValidMeterSerialNumber,
  meterSerialNumbersEnphaseGateway: isValidMeterSerialNumberEnphaseGateway,
  meterSerialNumbersEnphaseRelay: isValidMeterSerialNumberEnphaseRelay,
  modemSerialNumber: noop,
  dataloggerSerialNumber: noop,
  dataloggerCcSerialNumber: noop,
  communicationModuleSerialNumber: noop,
  batterySerialNumber: noop,
  kwhMeterSerialNumber: noop,
  layoutPlan: noop,
  inverterConfiguration: isValidAmountOfInverters,
  numberOfBus: isValidNumberOfBus
};

export default function isValidItem(
  item: DynamicDocumentItem,
  module?: InstallationModule
): boolean | string | string[] {
  if (!module) return false;

  const validator = validators[item.field as DynamicDocumentItemCustomType];

  return validator ? validator(module) : true;
}
